import { Plus, History, Circle } from 'lucide-react';

/**
 * Ce que l'installateur voit en arrivant : son volume, son équipe, qui a posé quoi.
 *
 * Différence de fond avec l'accueil poseur : l'installateur pilote des gens. Ses
 * compteurs agrègent toute l'équipe, et chaque chantier récent porte le nom de son
 * auteur — sans quoi la liste ne servirait qu'à compter, pas à suivre.
 */
export default function AccueilInstallateur({
  stats, user, t, onCreerPoseur, onVoirChantiers, onVoirEquipe,
}) {
  const poseurs = stats?.poseurs || [];
  const derniers = stats?.derniers_chantiers || [];

  // Un poseur est vert s'il est actif ET vu récemment, gris s'il est actif mais absent,
  // pâle s'il est désactivé. Les trois états sont distincts parce qu'ils appellent trois
  // réactions différentes : rien, relancer, réactiver.
  const teinte = (p) => (!p.active ? 'text-neutral-300 fill-neutral-300'
    : p.connected ? 'text-emerald-500 fill-emerald-500'
    : 'text-neutral-400 fill-neutral-400');
  const etat = (p) => (!p.active ? t('connection_disabled')
    : p.connected ? t('connection_connected') : t('connection_inactive'));
  const chantierMot = (n) => (n > 1 ? t('chantier_plural') : t('chantier_singular'));

  return (
    <div className="space-y-4" data-testid="installateur-home">
      <div className="bg-gradient-to-br from-marque to-marque-clair text-white rounded-xl p-5 shadow-md">
        <p className="text-[13px] text-white/60">{t('inst_welcome')}</p>
        <h1 className="text-xl font-semibold mt-0.5">{stats?.user_name || user?.name || '—'}</h1>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white/10 rounded-lg p-3">
            <p className="text-[10px] text-white/60 uppercase tracking-wider">{t('inst_stat_month')}</p>
            <p className="text-2xl font-bold mt-0.5" data-testid="inst-stat-month">
              {stats?.chantiers_ce_mois ?? '—'}
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="text-[10px] text-white/60 uppercase tracking-wider">{t('inst_stat_total')}</p>
            <p className="text-2xl font-bold mt-0.5" data-testid="inst-stat-total">
              {stats?.total_chantiers ?? '—'}
            </p>
          </div>
        </div>
      </div>

      {/* ⚠️ LE COMPTE « EN ATTENTE DE MATERIEL » A ETE RETIRE LE 10/09/2026.
          Un bandeau orange l affichait ici depuis le 30/08. Demande de Boubacar :
          « ils ne doivent pas voir en attente de materiel. Toutes ces infos remontent
          dans les alertes pour l admin operateur ». Le manque n a pas disparu, c est
          son destinataire qui change. Ne pas le remettre : il reviendrait doubler
          l alerte de l admin d une information que le terrain ne doit plus suivre. */}

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={onCreerPoseur}
          className="p-4 bg-white border border-neutral-200 rounded-xl hover:border-marque active:scale-[0.98] transition-all text-left"
          data-testid="inst-quick-create-poseur"
        >
          <Plus className="w-5 h-5 text-emerald-600 mb-1.5" />
          <p className="text-[13px] font-semibold text-neutral-800">{t('inst_quick_create_poseur_title')}</p>
          <p className="text-[10.5px] text-neutral-500 mt-0.5">{t('inst_quick_create_poseur_desc')}</p>
        </button>
        <button
          onClick={onVoirChantiers}
          className="p-4 bg-white border border-neutral-200 rounded-xl hover:border-marque active:scale-[0.98] transition-all text-left"
          data-testid="inst-quick-see-chantiers"
        >
          <History className="w-5 h-5 text-cyan-600 mb-1.5" />
          <p className="text-[13px] font-semibold text-neutral-800">{t('inst_quick_see_chantiers_title')}</p>
          <p className="text-[10.5px] text-neutral-500 mt-0.5">{t('inst_quick_see_chantiers_desc')}</p>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[13px] font-semibold text-neutral-800">{t('inst_my_poseurs')}</h2>
          <button
            onClick={onVoirEquipe}
            className="text-[11px] text-marque hover:underline font-medium min-h-[44px] -my-2 px-2 -mr-2 flex items-center"
            data-testid="inst-see-team-btn"
          >
            {t('inst_manage_team')} →
          </button>
        </div>
        {poseurs.length === 0 ? (
          <p className="text-[12px] text-neutral-400 py-3 text-center">{t('inst_no_poseurs')}</p>
        ) : (
          <ul className="space-y-2">
            {poseurs.slice(0, 5).map((p) => (
              <li key={p.id} className="flex items-center gap-2 py-1" data-testid={`inst-poseur-item-${p.id}`}>
                <Circle className={`w-2.5 h-2.5 flex-shrink-0 ${teinte(p)}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-medium text-neutral-800 truncate">{p.name}</p>
                  <p className="text-[10.5px] text-neutral-400">
                    {etat(p)} · {p.nb_chantiers} {chantierMot(p.nb_chantiers)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <h2 className="text-[13px] font-semibold text-neutral-800 mb-3">{t('inst_last_chantiers')}</h2>
        {derniers.length === 0 ? (
          <p className="text-[12px] text-neutral-400 py-3 text-center">{t('inst_no_last_chantiers')}</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {derniers.map((c) => (
              <li key={c.client_id} className="py-2.5" data-testid={`inst-last-chantier-${c.client_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-neutral-800 truncate">{c.client_nom || '—'}</p>
                    <p className="text-[10.5px] text-neutral-500 truncate">
                      {c.reference_chantier || '—'} ·{' '}
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}
                    </p>
                  </div>
                  {/* Un chantier dont l'auteur a été supprimé n'affiche pas « Par » tout
                      seul : mieux vaut ne rien dire que d'annoncer un auteur vide. */}
                  {c.created_by_name ? (
                    <span className="text-[10px] text-neutral-500 whitespace-nowrap">
                      {t('inst_by')} {c.created_by_name}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
